// frontend/src/components/DocsPage.jsx

import React, { useState, useEffect } from 'react';
import { Copy, Check, Menu, X } from 'lucide-react';

/* ── 程式碼區塊元件（含複製按鈕）───────────────────── */

function CodeBlock({ code, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(code); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-3 rounded-xl overflow-hidden">
      {language && (
        <div className="bg-[#2A2560] px-4 py-1.5 text-xs text-indigo-300/70">{language}</div>
      )}
      <button
        onClick={handleCopy}
        className="absolute right-2 top-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-indigo-200 opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="複製程式碼"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
      <pre className="bg-ink text-indigo-50 p-4 overflow-x-auto text-sm leading-relaxed"
        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

/* ── 多語言分頁程式碼元件 ────────────────────────── */

function TabbedCode({ tabs }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(tabs[active].code); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl overflow-hidden my-4">
      <div className="flex items-center bg-[#2A2560] px-2 pt-1">
        {tabs.map((tab, i) => (
          <button
            key={tab.label}
            onClick={() => { setActive(i); setCopied(false); }}
            className={`px-3 py-2 text-xs font-medium rounded-t transition-colors ${
              active === i ? 'bg-ink text-white' : 'text-indigo-300/70 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
        <button
          onClick={handleCopy}
          className="ml-auto p-1.5 mr-1 rounded text-indigo-300/70 hover:text-white hover:bg-white/10 transition-colors"
          title="複製程式碼"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <pre className="bg-ink text-indigo-50 p-4 overflow-x-auto text-sm leading-relaxed"
        style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace" }}
      >
        <code>{tabs[active].code}</code>
      </pre>
    </div>
  );
}

/* ── 目錄結構 ────────────────────────────────────── */

const tocSections = [
  { id: 'overview',      title: '服務總覽' },
  { id: 'models',        title: '模型說明' },
  { id: 'webui-guide',   title: 'WebUI 使用指南' },
  { id: 'api',           title: 'OpenAI 相容 API' },
  { id: 'code-examples', title: '程式碼範例' },
  { id: 'websocket',     title: 'WebSocket API' },
  { id: 'integrations',  title: '第三方整合' },
  { id: 'errors',        title: '錯誤處理' },
  { id: 'performance',   title: '效能建議' },
  { id: 'system-info',   title: '系統資訊' },
];

/* ── 主元件 ──────────────────────────────────────── */

export default function DocsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  // 追蹤目前可見的段落
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );
    tocSections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const SidebarNav = () => (
    <nav className="space-y-0.5">
      <p className="px-3 py-2 text-xs font-semibold text-indigo-300/70 uppercase tracking-wider">目錄</p>
      {tocSections.map(({ id, title }) => (
        <button
          key={id}
          onClick={() => scrollTo(id)}
          className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${
            activeSection === id
              ? 'bg-brand-soft text-brand font-medium'
              : 'text-ink-muted hover:text-ink hover:bg-brand-soft'
          }`}
        >
          {title}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="lg:flex lg:gap-8">

        {/* 桌面版左側目錄 */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-20">
            <SidebarNav />
          </div>
        </aside>

        {/* 手機版可收合目錄 */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex items-center space-x-2 px-3 py-2 bg-white rounded-xl shadow-sm border text-sm font-medium text-ink/80 hover:bg-paper"
          >
            {mobileMenuOpen ? <X size={16} /> : <Menu size={16} />}
            <span>目錄導航</span>
          </button>
          {mobileMenuOpen && (
            <div className="mt-2 bg-white rounded-xl shadow-lg border p-3">
              <SidebarNav />
            </div>
          )}
        </div>

        {/* 主要內容 */}
        <main className="flex-1 min-w-0 space-y-8">

          {/* ════════════════════════════════════════════
              1. 服務總覽
              ════════════════════════════════════════════ */}
          <section id="overview" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">服務總覽</h2>
            <p className="text-ink-muted mb-4">
              Whisper-WebUI 是一個基於 OpenAI Whisper 的語音轉文字網頁應用程式，
              採用前後端分離架構，支援 Docker 部署與 GPU 加速。提供網頁介面即時轉錄、
              OpenAI 相容 REST API，以及 WebSocket 即時串流等多種使用方式。
            </p>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">功能特色</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { title: '多模型支援', desc: 'Breeze 繁中優化、台語模型、Whisper turbo / large-v3 / medium' },
                { title: '多種輸入方式', desc: '上傳音訊/影片檔案、YouTube URL（即時下載進度）、支援短音檔（2-3 秒）' },
                { title: '說話者分離', desc: '使用 pyannote.audio 辨識不同說話者，以顏色標籤區分（需 HuggingFace Token）' },
                { title: '即時回饋', desc: 'WebSocket 即時顯示下載進度、轉錄進度、說話者分離狀態與預估剩餘時間' },
                { title: 'OpenAI 相容 API', desc: '完全相容 OpenAI Whisper API，可直接整合第三方應用' },
                { title: '音訊優化', desc: '自動單聲道轉換、採樣率標準化、音量正規化' },
                { title: '多格式輸出', desc: '支援 JSON、TXT、SRT、VTT 等多種輸出格式（含說話者標註）' },
              ].map((item) => (
                <div key={item.title} className="bg-paper rounded-xl p-3 border">
                  <h4 className="font-medium text-ink">{item.title}</h4>
                  <p className="text-sm text-ink-muted mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">系統架構</h3>
            <CodeBlock language="text" code={`new-whisper-webui-docker/
├── backend/                 # 後端服務 (FastAPI + Whisper)
│   ├── app.py              # FastAPI 主程式
│   ├── models/             # 預載模型目錄
│   └── requirements.txt    # Python 依賴
├── frontend/               # 前端服務 (React + Tailwind)
│   ├── src/App.jsx         # React 主元件
│   ├── nginx.conf          # Nginx 反向代理設定
│   └── package.json        # Node.js 依賴
└── docker-compose.yml      # Docker Compose 編排檔`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">技術堆疊</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-ink/80 mb-2">後端</h4>
                <ul className="text-sm text-ink-muted space-y-1">
                  <li>• FastAPI — Web 框架</li>
                  <li>• OpenAI Whisper — 語音辨識</li>
                  <li>• Transformers — Breeze ASR 模型</li>
                  <li>• pyannote.audio — 說話者分離</li>
                  <li>• PyTorch + CUDA — GPU 加速</li>
                  <li>• yt-dlp + Deno — YouTube 下載</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-ink/80 mb-2">前端</h4>
                <ul className="text-sm text-ink-muted space-y-1">
                  <li>• React 18 — UI 框架</li>
                  <li>• TailwindCSS — 樣式</li>
                  <li>• Axios — HTTP 請求</li>
                  <li>• Lucide React — 圖示庫</li>
                  <li>• Nginx — 靜態伺服器與反向代理</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ════════════════════════════════════════════
              2. 模型說明
              ════════════════════════════════════════════ */}
          <section id="models" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">模型說明</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">中文優化模型（推薦）</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-brand-soft">
                    <th className="text-left p-3 font-semibold border-b">模型 ID</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                    <th className="text-left p-3 font-semibold border-b">開發者</th>
                    <th className="text-center p-3 font-semibold border-b">速度</th>
                    <th className="text-center p-3 font-semibold border-b">品質</th>
                    <th className="text-left p-3 font-semibold border-b">建議用途</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-brand px-1.5 py-0.5 rounded text-xs">breeze</code></td>
                    <td className="p-3">Breeze ASR 25 繁中優化</td>
                    <td className="p-3">MediaTek</td>
                    <td className="p-3 text-center">⚡⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="p-3">繁體中文、中英混用（推薦）</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-brand px-1.5 py-0.5 rounded text-xs">taiwanese</code></td>
                    <td className="p-3">Breeze ASR 26 台語模型</td>
                    <td className="p-3">MediaTek Research</td>
                    <td className="p-3 text-center">⚡⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="p-3">台灣閩南語語音</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">OpenAI Whisper 模型</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">模型 ID</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                    <th className="text-center p-3 font-semibold border-b">速度</th>
                    <th className="text-center p-3 font-semibold border-b">品質</th>
                    <th className="text-left p-3 font-semibold border-b">建議用途</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-ink px-1.5 py-0.5 rounded text-xs">whisper-1</code></td>
                    <td className="p-3">預設（對應 large-v3-turbo）</td>
                    <td className="p-3 text-center">⚡⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="p-3">通用多語言</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-ink px-1.5 py-0.5 rounded text-xs">whisper-large-v3-turbo</code></td>
                    <td className="p-3">OpenAI 2024 最新快速模型</td>
                    <td className="p-3 text-center">⚡⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐⭐</td>
                    <td className="p-3">速度與品質平衡</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-ink px-1.5 py-0.5 rounded text-xs">whisper-large-v3</code></td>
                    <td className="p-3">OpenAI 最高品質模型</td>
                    <td className="p-3 text-center">⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐⭐⭐</td>
                    <td className="p-3">追求最高準確度</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-ink px-1.5 py-0.5 rounded text-xs">whisper-medium</code></td>
                    <td className="p-3">中型通用模型</td>
                    <td className="p-3 text-center">⚡⚡⚡</td>
                    <td className="p-3 text-center">⭐⭐⭐</td>
                    <td className="p-3">一般用途</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">模型別名對照</h3>
            <p className="text-sm text-ink-muted mb-2">以下名稱皆可在 API 中使用：</p>
            <CodeBlock code={`breeze                 → breeze (繁中推薦)
taiwanese              → taiwanese
breeze-asr-26          → taiwanese
taigi                  → taiwanese
whisper-1              → large-v3-turbo
whisper-large-v3-turbo → large-v3-turbo
turbo                  → large-v3-turbo
whisper-large-v3       → large-v3
large-v3               → large-v3
whisper-medium         → medium
medium                 → medium`} />
          </section>

          {/* ════════════════════════════════════════════
              3. WebUI 使用指南
              ════════════════════════════════════════════ */}
          <section id="webui-guide" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">WebUI 使用指南</h2>
            <p className="text-ink-muted mb-4">透過網頁介面，您可以輕鬆完成語音轉文字作業。以下是操作步驟：</p>

            <div className="space-y-4">
              {[
                { step: 1, title: '選擇模型', desc: 'Breeze ASR 25（繁體中文推薦）、Breeze ASR 26 台語模型、Whisper large-v3-turbo（速度快）、Whisper large-v3（最高品質）、Whisper medium（通用）。' },
                { step: 2, title: '選擇語言', desc: '中文 (zh)、台語、英文 (en)、日文 (ja)、西班牙文 (es)。選擇「台語」會自動切換到 Breeze ASR 26（唯一支援台語的模型），反之亦然。Whisper 系列另有「自動偵測」（auto），不指定語言、交給模型自己判斷，適合多語或不確定內容的音檔。' },
                { step: 3, title: '選擇任務', desc: '「轉錄」保留原始語言輸出；「翻譯」將音訊翻譯為英文。' },
                { step: 4, title: '上傳檔案', desc: '拖放檔案至上傳區，或點擊選擇檔案。也可輸入 YouTube URL（會即時顯示下載進度與速度）。支援 WAV、MP3、M4A、MP4 等格式。' },
                { step: 5, title: '調整進階設定（選用）', desc: '「音訊優化」把音檔轉成單聲道 16 kHz 並正規化音量（只需幾秒，建議開啟）；「說話者分離」辨識並標示不同說話者（最多 6 位），額外約 1% 時間。' },
                { step: 6, title: '開始轉錄', desc: '點擊「開始轉錄」按鈕，系統將透過 WebSocket 即時顯示下載進度、轉錄進度與預估剩餘時間。若啟用說話者分離，轉錄完成後會自動進行分離處理。' },
                { step: 7, title: '編輯字幕（選用）', desc: '轉錄完成後會出現字幕編輯器：播放影片時目前字幕會自動高亮並捲動；雙擊可修改文字，± 按鈕微調 0.1 秒，也能將時間設為目前播放位置；支援分割、合併、插入、刪除與 50 步復原/重做。' },
                { step: 8, title: '下載結果', desc: '可下載 TXT 純文字或 SRT 字幕格式，內容會包含您在編輯器中的所有修改。若有說話者資訊，下載檔案會自動加上 [說話者 A] 等標註。' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-brand-soft text-brand rounded-full flex items-center justify-center font-bold text-sm">
                    {step}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-ink">{title}</h4>
                    <p className="text-sm text-ink-muted mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-brand-soft border border-brand-border rounded-xl p-4">
              <h4 className="font-medium text-brand mb-1">小提示</h4>
              <ul className="text-sm text-ink/75 space-y-1">
                <li>• 字幕編輯器支援鍵盤快速鍵：空白鍵播放/暫停、方向鍵前後跳轉或切換句子、Enter 編輯目前句、Ctrl+Z 復原。按 <code className="bg-white px-1 rounded text-xs">?</code> 可展開完整清單。</li>
                <li>• 選擇「台語」語言會自動綁定 Breeze ASR 26；該模型輸出為中文漢字而非台語正字。</li>
                <li>• 短音檔（小於 10 秒）會自動切換為直接模型推論模式。</li>
                <li>• 如瀏覽器重新整理，系統會嘗試恢復進行中的轉錄任務。</li>
                <li>• 說話者分離功能需要設定 <code className="bg-white px-1 rounded text-xs">HF_TOKEN</code> 環境變數（HuggingFace Token），並需在 HuggingFace 同意 pyannote 相關模型的使用條款。</li>
                <li>• 說話者分離在轉錄完成後進行，不會阻塞其他使用者的轉錄任務。</li>
                <li>• YouTube URL 下載時會即時顯示下載百分比與速度。</li>
              </ul>
            </div>
          </section>

          {/* ════════════════════════════════════════════
              4. OpenAI 相容 API
              ════════════════════════════════════════════ */}
          <section id="api" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">OpenAI 相容 API</h2>
            <p className="text-ink-muted mb-4">
              本服務提供與 OpenAI Whisper API 完全相容的 RESTful 端點，可無縫整合至任何支援 OpenAI API 的應用程式。
              <strong>不需要 API Key</strong>。
            </p>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">端點總覽</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">方法</th>
                    <th className="text-left p-3 font-semibold border-b">端點</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs">GET</code></td>
                    <td className="p-3"><code className="text-sm">/v1/models</code></td>
                    <td className="p-3">列出可用模型</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="bg-brand-soft text-brand px-1.5 py-0.5 rounded text-xs">POST</code></td>
                    <td className="p-3"><code className="text-sm">/v1/audio/transcriptions</code></td>
                    <td className="p-3">音頻轉錄</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* GET /v1/models */}
            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">
              <code className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs mr-2">GET</code>
              /v1/models
            </h3>
            <p className="text-sm text-ink-muted mb-3">取得伺服器支援的所有模型列表。</p>
            <CodeBlock language="回應範例" code={`{
  "object": "list",
  "data": [
    {"id": "whisper-1", "object": "model", "owned_by": "openai"},
    {"id": "whisper-large-v3-turbo", "object": "model", "owned_by": "openai"},
    {"id": "whisper-large-v3", "object": "model", "owned_by": "openai"},
    {"id": "whisper-medium", "object": "model", "owned_by": "openai"},
    {"id": "breeze", "object": "model", "owned_by": "mediatek"},
    {"id": "taiwanese", "object": "model", "owned_by": "mediatek"}
  ]
}`} />

            {/* POST /v1/audio/transcriptions */}
            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">
              <code className="bg-brand-soft text-brand px-1.5 py-0.5 rounded text-xs mr-2">POST</code>
              /v1/audio/transcriptions
            </h3>
            <p className="text-sm text-ink-muted mb-3">將音頻檔案轉換為文字。請求格式為 <code className="bg-brand-soft px-1 rounded text-xs">multipart/form-data</code>。</p>

            <h4 className="font-medium text-ink/80 mt-4 mb-2">請求參數</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">參數</th>
                    <th className="text-left p-3 font-semibold border-b">類型</th>
                    <th className="text-center p-3 font-semibold border-b">必填</th>
                    <th className="text-left p-3 font-semibold border-b">預設值</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'file', type: 'file', required: true, def: '-', desc: '音訊檔案（MP3, WAV, M4A, MP4, FLAC, OGG, WebM）' },
                    { name: 'model', type: 'string', required: false, def: 'whisper-1', desc: '模型名稱' },
                    { name: 'language', type: 'string', required: false, def: 'zh', desc: '音訊語言（ISO-639-1 格式）。台語可送 nan 或 zh，兩者等價：Breeze ASR 26 一律以 zh 解碼並輸出中文漢字。' },
                    { name: 'response_format', type: 'string', required: false, def: 'json', desc: '回應格式：json / text / verbose_json / srt / vtt' },
                    { name: 'temperature', type: 'float', required: false, def: '0.0', desc: '取樣溫度（0-1）' },
                    { name: 'prompt', type: 'string', required: false, def: '-', desc: '提示詞（引導風格）' },
                  ].map((row) => (
                    <tr key={row.name} className="border-b hover:bg-paper">
                      <td className="p-3"><code className="bg-brand-soft px-1.5 py-0.5 rounded text-xs">{row.name}</code></td>
                      <td className="p-3 text-ink-muted">{row.type}</td>
                      <td className="p-3 text-center">{row.required ? '✅' : '❌'}</td>
                      <td className="p-3 text-ink-muted"><code className="text-xs">{row.def}</code></td>
                      <td className="p-3 text-ink-muted">{row.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-ink-muted mt-2">檔案大小限制：最大 200MB</p>

            <h4 className="font-medium text-ink/80 mt-6 mb-2">回應格式範例</h4>

            <TabbedCode tabs={[
              { label: 'json（預設）', code: `{
  "text": "轉錄的完整文字內容。"
}` },
              { label: 'verbose_json', code: `{
  "task": "transcribe",
  "language": "zh",
  "duration": 65.5,
  "text": "轉錄的完整文字內容。",
  "segments": [
    {
      "id": 0,
      "start": 0.0,
      "end": 5.28,
      "text": "第一段文字。"
    },
    {
      "id": 1,
      "start": 5.28,
      "end": 10.56,
      "text": "第二段文字。"
    }
  ]
}` },
              { label: 'srt', code: `1
00:00:00,000 --> 00:00:05,280
第一段文字。

2
00:00:05,280 --> 00:00:10,560
第二段文字。` },
              { label: 'vtt', code: `WEBVTT

00:00:00.000 --> 00:00:05.280
第一段文字。

00:00:05.280 --> 00:00:10.560
第二段文字。` },
            ]} />
          </section>

          {/* ════════════════════════════════════════════
              5. 程式碼範例
              ════════════════════════════════════════════ */}
          <section id="code-examples" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">程式碼範例</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">基本轉錄</h3>
            <TabbedCode tabs={[
              { label: 'cURL', code: `curl -X POST https://whisper.chih-hao.synology.me/v1/audio/transcriptions \\
  -F "file=@audio.mp3" \\
  -F "model=breeze"` },
              { label: 'Python', code: `from openai import OpenAI

client = OpenAI(
    api_key="not-needed",  # 本服務不需要 API Key
    base_url="https://whisper.chih-hao.synology.me/v1"
)

with open("audio.mp3", "rb") as audio_file:
    transcript = client.audio.transcriptions.create(
        model="breeze",
        file=audio_file
    )
    print(transcript.text)` },
              { label: 'Node.js', code: `const OpenAI = require('openai');
const fs = require('fs');

const openai = new OpenAI({
  apiKey: ***
  baseURL: 'https://whisper.chih-hao.synology.me/v1'
});

async function transcribe() {
  const transcript = await openai.audio.transcriptions.create({
    file: fs.createReadStream('audio.mp3'),
    model: 'breeze',
    language: 'zh'
  });
  console.log(transcript.text);
}

transcribe();` },
              { label: 'PHP', code: `<?php
$url = 'https://whisper.chih-hao.synology.me/v1/audio/transcriptions';

$postFields = [
    'file' => new CURLFile('audio.mp3'),
    'model' => 'breeze',
    'language' => 'zh'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $postFields);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

$result = json_decode($response, true);
echo $result['text'];` },
            ]} />

            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">更多 cURL 範例</h3>

            <h4 className="font-medium text-ink/80 mt-4 mb-2">台語轉錄</h4>
            <CodeBlock language="bash" code={`curl -X POST https://whisper.chih-hao.synology.me/v1/audio/transcriptions \\
  -F "file=@taiwanese_audio.mp3" \\
  -F "model=taiwanese"`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">下載 SRT 字幕</h4>
            <CodeBlock language="bash" code={`curl -X POST https://whisper.chih-hao.synology.me/v1/audio/transcriptions \\
  -F "file=@video.mp4" \\
  -F "model=breeze" \\
  -F "response_format=srt" \\
  -o subtitle.srt`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">取得詳細時間戳</h4>
            <CodeBlock language="bash" code={`curl -X POST https://whisper.chih-hao.synology.me/v1/audio/transcriptions \\
  -F "file=@audio.mp3" \\
  -F "model=breeze" \\
  -F "response_format=verbose_json" | jq`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">Python — 使用 requests</h3>
            <CodeBlock language="python" code={`import requests

url = "https://whisper.chih-hao.synology.me/v1/audio/transcriptions"

# 基本轉錄
with open("audio.mp3", "rb") as f:
    response = requests.post(
        url,
        files={"file": f},
        data={"model": "breeze"}
    )
    print(response.json()["text"])

# 取得 SRT 字幕
with open("audio.mp3", "rb") as f:
    response = requests.post(
        url,
        files={"file": f},
        data={
            "model": "breeze",
            "response_format": "srt"
        }
    )
    with open("subtitle.srt", "w", encoding="utf-8") as srt:
        srt.write(response.text)`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">Node.js — 使用 fetch</h3>
            <CodeBlock language="javascript" code={`const fs = require('fs');
const FormData = require('form-data');

async function transcribe() {
  const form = new FormData();
  form.append('file', fs.createReadStream('audio.mp3'));
  form.append('model', 'breeze');

  const response = await fetch(
    'https://whisper.chih-hao.synology.me/v1/audio/transcriptions',
    { method: 'POST', body: form }
  );

  const result = await response.json();
  console.log(result.text);
}

transcribe();`} />
          </section>

          {/* ════════════════════════════════════════════
              6. WebSocket API
              ════════════════════════════════════════════ */}
          <section id="websocket" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">WebSocket API</h2>
            <p className="text-ink-muted mb-4">
              WebSocket API 提供即時轉錄功能，適合需要進度回饋的場景。使用前須先透過 REST API 上傳檔案取得 <code className="bg-brand-soft px-1 rounded text-xs">upload_id</code>。
            </p>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">端點</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">端點</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="text-sm">POST /upload/</code></td>
                    <td className="p-3">上傳音訊檔案或 YouTube URL，取得 upload_id</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="text-sm">GET /status/{'{upload_id}'}</code></td>
                    <td className="p-3">查詢任務是否仍在進行中</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="text-sm">POST /upload/reuse/{'{upload_id}'}</code></td>
                    <td className="p-3">沿用上次轉錄過的音檔開新任務（保留 24 小時），回新的 upload_id；音檔已清除回 410</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="text-sm">POST /cancel/{'{upload_id}'}</code></td>
                    <td className="p-3">取消任務（排隊中離開佇列、轉錄中砍掉 worker），回 <code className="text-xs">{'{"canceled": true}'}</code></td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3"><code className="text-sm">WS /ws/transcribe/</code></td>
                    <td className="p-3">建立 WebSocket 轉錄連線</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">客戶端發送 — 開始轉錄</h3>
            <CodeBlock language="json" code={`{
  "upload_id": "whisper_up_xxxxx",
  "model": "breeze",
  "language": "zh",
  "task": "transcribe",
  "use_audio_optimization": true,
  "use_diarization": true
}`} />
            <p className="text-xs text-ink-muted mt-2">
              <code className="bg-brand-soft px-1 rounded">use_diarization</code>：啟用說話者分離（預設 false），轉錄完成後會自動進行說話者辨識。
            </p>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">取消任務</h3>
            <p className="text-sm text-ink-muted mb-2">
              請用 <code className="bg-brand-soft px-1 rounded text-xs">POST /cancel/{'{upload_id}'}</code>，
              不要走 WebSocket——連線進入訂閱迴圈後就不再讀客戶端訊息，
              送 <code className="bg-brand-soft px-1 rounded text-xs">{'{"action":"cancel"}'}</code> 要等整份工作結束才會被看到。
              取消成功時所有訂閱者會收到 <code className="bg-brand-soft px-1 rounded text-xs">{'{"canceled": true}'}</code>。
            </p>
            <CodeBlock language="bash" code={`curl -X POST https://whisper.chih-hao.synology.me/cancel/whisper_up_xxxxx`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">伺服器回應</h3>

            <h4 className="font-medium text-ink/80 mt-4 mb-2">YouTube 下載中</h4>
            <CodeBlock language="json" code={`{
  "status": "downloading",
  "message": "正在下載 YouTube 音訊...",
  "percent": 45.2,
  "speed": "2.5 MiB/s",
  "eta": "00:05"
}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">YouTube 下載完成</h4>
            <CodeBlock language="json" code={`{"status": "download_complete", "message": "YouTube 音訊下載完成"}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">音訊優化中</h4>
            <CodeBlock language="json" code={`{"status": "optimizing"}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">優化完成</h4>
            <CodeBlock language="json" code={`{"status": "optimization_complete"}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">預估時間</h4>
            <CodeBlock language="json" code={`{"estimated_time": 30.5}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">說話者分離中</h4>
            <CodeBlock language="json" code={`{"status": "diarizing", "message": "正在進行說話者分離..."}`} />

            <h4 className="font-medium text-ink/80 mt-4 mb-2">轉錄完成（含說話者資訊）</h4>
            <CodeBlock language="json" code={`{
  "done": true,
  "text": "完整轉錄文字...",
  "segments": [
    {"id": 0, "start": 0.0, "end": 2.5, "text": "第一段文字", "speaker": "說話者 A"},
    {"id": 1, "start": 2.5, "end": 5.0, "text": "第二段文字", "speaker": "說話者 B"}
  ]
}`} />
            <p className="text-xs text-ink-muted mt-2">
              <code className="bg-brand-soft px-1 rounded">speaker</code> 欄位僅在啟用說話者分離時出現。未啟用時，segments 格式與原本相同。
            </p>

            <h4 className="font-medium text-ink/80 mt-4 mb-2">非致命警告</h4>
            <CodeBlock language="json" code={`{"warning": "說話者分離失敗: 錯誤訊息..."}`} />
            <p className="text-xs text-ink-muted mt-2">說話者分離失敗為非致命錯誤，轉錄結果仍會正常返回，但不包含說話者資訊。</p>

          </section>

          {/* ════════════════════════════════════════════
              7. 第三方整合
              ════════════════════════════════════════════ */}
          <section id="integrations" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">第三方整合</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">OpenClaw</h3>
            <CodeBlock language="bash" code={`# 設定 API 位址
openclaw config set tools.media.audio.baseUrl "https://whisper.chih-hao.synology.me/v1"`} />
            <p className="text-sm text-ink-muted mt-2 mb-2">
              完整設定檔範例（<code className="bg-brand-soft px-1 rounded text-xs">~/.openclaw/openclaw.json</code>）：
            </p>
            <CodeBlock language="json" code={`{
  "tools": {
    "media": {
      "audio": {
        "enabled": true,
        "baseUrl": "https://whisper.chih-hao.synology.me/v1",
        "models": [
          {
            "provider": "openai",
            "model": "breeze",
            "capabilities": ["audio"],
            "language": "zh"
          }
        ]
      }
    }
  }
}`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">n8n</h3>
            <div className="bg-paper rounded-xl p-4 border text-sm text-ink/80 space-y-2">
              <p className="font-medium">設定步驟：</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>新增 <strong>HTTP Request</strong> 節點</li>
                <li>Method：<code className="bg-brand-soft px-1 rounded text-xs">POST</code></li>
                <li>URL：<code className="bg-brand-soft px-1 rounded text-xs">https://whisper.chih-hao.synology.me/v1/audio/transcriptions</code></li>
                <li>Body Content Type：<code className="bg-brand-soft px-1 rounded text-xs">Multipart Form Data</code></li>
                <li>Body Parameters：
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li><code className="bg-brand-soft px-1 rounded text-xs">file</code>：（Binary Data）</li>
                    <li><code className="bg-brand-soft px-1 rounded text-xs">model</code>：<code className="bg-brand-soft px-1 rounded text-xs">breeze</code></li>
                  </ul>
                </li>
              </ol>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-8 mb-3">Make（Integromat）</h3>
            <div className="bg-paper rounded-xl p-4 border text-sm text-ink/80 space-y-2">
              <p className="font-medium">設定步驟：</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>新增 <strong>HTTP</strong> 模組</li>
                <li>URL：<code className="bg-brand-soft px-1 rounded text-xs">https://whisper.chih-hao.synology.me/v1/audio/transcriptions</code></li>
                <li>Method：<code className="bg-brand-soft px-1 rounded text-xs">POST</code></li>
                <li>Body type：<code className="bg-brand-soft px-1 rounded text-xs">Multipart/form-data</code></li>
                <li>加入欄位：<code className="bg-brand-soft px-1 rounded text-xs">file</code>（檔案）、<code className="bg-brand-soft px-1 rounded text-xs">model</code>（文字：breeze）</li>
              </ol>
            </div>
          </section>

          {/* ════════════════════════════════════════════
              8. 錯誤處理
              ════════════════════════════════════════════ */}
          <section id="errors" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">錯誤處理</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">錯誤回應格式</h3>
            <CodeBlock language="json" code={`{
  "detail": "錯誤訊息說明"
}`} />

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">常見錯誤碼</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-red-50">
                    <th className="text-left p-3 font-semibold border-b">HTTP 狀態碼</th>
                    <th className="text-left p-3 font-semibold border-b">說明</th>
                    <th className="text-left p-3 font-semibold border-b">可能原因</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { code: '400', name: 'Bad Request', reason: '缺少必要參數、不支援的檔案格式' },
                    { code: '413', name: 'Payload Too Large', reason: '檔案超過 200MB 限制' },
                    { code: '500', name: 'Internal Server Error', reason: '伺服器內部錯誤、模型載入失敗' },
                    { code: '503', name: 'Service Unavailable', reason: '伺服器負載過高' },
                  ].map((row) => (
                    <tr key={row.code} className="border-b hover:bg-paper">
                      <td className="p-3"><code className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs">{row.code}</code></td>
                      <td className="p-3">{row.name}</td>
                      <td className="p-3 text-ink-muted">{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">錯誤處理範例</h3>
            <CodeBlock language="python" code={`import requests

try:
    response = requests.post(
        "https://whisper.chih-hao.synology.me/v1/audio/transcriptions",
        files={"file": open("audio.mp3", "rb")},
        data={"model": "breeze"},
        timeout=300  # 設定超時時間（秒）
    )
    response.raise_for_status()
    print(response.json()["text"])
except requests.exceptions.Timeout:
    print("請求超時，請稍後再試")
except requests.exceptions.HTTPError as e:
    print(f"HTTP 錯誤: {e.response.status_code}")
    print(f"錯誤訊息: {e.response.json().get('detail', '未知錯誤')}")
except Exception as e:
    print(f"發生錯誤: {e}")`} />
          </section>

          {/* ════════════════════════════════════════════
              9. 效能建議
              ════════════════════════════════════════════ */}
          <section id="performance" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">效能建議</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">處理速度參考</h3>
            <p className="text-sm text-ink-muted mb-3">以 RTX 6000 Ada 48GB 為例：</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">模型</th>
                    <th className="text-center p-3 font-semibold border-b">30 秒音檔</th>
                    <th className="text-center p-3 font-semibold border-b">5 分鐘音檔</th>
                    <th className="text-center p-3 font-semibold border-b">1 小時音檔</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { model: 'breeze（推薦）', t30: '~8 秒', t5m: '~1.2 分鐘', t1h: '~15 分鐘' },
                    { model: 'taiwanese', t30: '~15 秒', t5m: '~2.5 分鐘', t1h: '~30 分鐘' },
                    { model: 'whisper-1 (turbo)', t30: '~5 秒', t5m: '~50 秒', t1h: '~10 分鐘' },
                    { model: 'whisper-large-v3', t30: '~15 秒', t5m: '~2.5 分鐘', t1h: '~30 分鐘' },
                    { model: 'whisper-medium', t30: '~10 秒', t5m: '~1.5 分鐘', t1h: '~20 分鐘' },
                  ].map((row) => (
                    <tr key={row.model} className="border-b hover:bg-paper">
                      <td className="p-3 font-medium">{row.model}</td>
                      <td className="p-3 text-center">{row.t30}</td>
                      <td className="p-3 text-center">{row.t5m}</td>
                      <td className="p-3 text-center">{row.t1h}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">最佳實踐</h3>
            <div className="space-y-3">
              {[
                { title: '選擇適當模型', items: ['繁體中文 → breeze（推薦）', '台語 → taiwanese', '多語言通用 → whisper-1（large-v3-turbo）', '追求最高品質 → whisper-large-v3'] },
                { title: '音訊預處理', items: ['服務會自動優化音訊（轉單聲道、16kHz 重採樣）', '如需更快速度，可預先轉換為 16kHz mono WAV'] },
                { title: '檔案大小', items: ['建議單檔不超過 100MB', '長音檔可考慮切分後分批處理'] },
                { title: '短音檔支援', items: ['支援 2-3 秒的簡短語音辨識', '短音檔（<10秒）自動切換直接模型推論模式', '無需特殊設定，API 會自動處理'] },
                { title: '超時設定', items: ['建議設定較長的超時時間（5-10 分鐘）', '長音檔處理可能需要較長時間'] },
              ].map(({ title, items }) => (
                <div key={title} className="bg-paper rounded-xl p-4 border">
                  <h4 className="font-medium text-ink mb-2">{title}</h4>
                  <ul className="text-sm text-ink-muted space-y-1">
                    {items.map((item, i) => <li key={i}>• {item}</li>)}
                  </ul>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">記憶體管理</h3>
            <p className="text-sm text-ink-muted">系統內建智慧記憶體管理機制：</p>
            <ul className="text-sm text-ink-muted mt-2 space-y-1">
              <li>• Whisper 模型閒置 5 分鐘後自動釋放</li>
              <li>• 說話者分離 (pyannote) 模型閒置 5 分鐘後自動釋放</li>
              <li>• 記憶體使用超過 8GB 時主動清理</li>
              <li>• 動態調整併發任務數量</li>
              <li>• 說話者分離在轉錄完成後才執行，不佔用轉錄併發名額</li>
            </ul>
          </section>

          {/* ════════════════════════════════════════════
              10. 系統資訊
              ════════════════════════════════════════════ */}
          <section id="system-info" className="scroll-mt-20 bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-2xl font-bold text-ink mb-4">系統資訊</h2>

            <h3 className="text-lg font-semibold text-ink/80 mb-3">伺服器規格</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <tbody>
                  {[
                    { label: 'CPU', value: 'Intel Core i5-13500' },
                    { label: 'GPU', value: 'NVIDIA RTX 6000 Ada Generation 48GB' },
                    { label: '作業系統', value: 'Unraid 7.0.1（Docker 部署）' },
                    { label: 'CUDA / PyTorch', value: 'CUDA 12.8 · PyTorch 2.11 (cu128)' },
                  ].map((row) => (
                    <tr key={row.label} className="border-b hover:bg-paper">
                      <td className="p-3 font-medium text-ink/80 w-32">{row.label}</td>
                      <td className="p-3 text-ink-muted">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">版本資訊</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <tbody>
                  {[
                    { label: 'API 版本', value: 'v1' },
                    { label: '相容性', value: 'OpenAI Whisper API' },
                    { label: '最後更新', value: '2026-08-19' },
                  ].map((row) => (
                    <tr key={row.label} className="border-b hover:bg-paper">
                      <td className="p-3 font-medium text-ink/80 w-32">{row.label}</td>
                      <td className="p-3 text-ink-muted">{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">連接埠對應</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-paper">
                    <th className="text-left p-3 font-semibold border-b">服務</th>
                    <th className="text-left p-3 font-semibold border-b">容器埠</th>
                    <th className="text-left p-3 font-semibold border-b">主機埠</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3">前端（Nginx）</td>
                    <td className="p-3">80</td>
                    <td className="p-3">7861</td>
                  </tr>
                  <tr className="border-b hover:bg-paper">
                    <td className="p-3">後端（FastAPI）</td>
                    <td className="p-3">8000</td>
                    <td className="p-3">8000</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">聯絡資訊</h3>
            <div className="bg-paper rounded-xl p-4 border">
              <p className="text-sm text-ink/80"><strong>作者：</strong>張志豪</p>
              <p className="text-sm text-ink/80 mt-1"><strong>專案：</strong>Whisper-WebUI</p>
            </div>

            <h3 className="text-lg font-semibold text-ink/80 mt-6 mb-3">參考資源</h3>
            <ul className="text-sm text-ink-muted space-y-1">
              <li>• <a href="https://github.com/openai/whisper" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">OpenAI Whisper</a></li>
              <li>• <a href="https://huggingface.co/MediaTek-Research/Breeze-ASR-25" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">MediaTek Breeze ASR</a></li>
              <li>• <a href="https://huggingface.co/MediaTek-Research/Breeze-ASR-26" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">MediaTek Breeze ASR 26（台語）</a></li>
              <li>• <a href="https://github.com/pyannote/pyannote-audio" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">pyannote.audio（說話者分離）</a></li>
              <li>• <a href="https://fastapi.tiangolo.com/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">FastAPI</a></li>
              <li>• <a href="https://react.dev/" target="_blank" rel="noopener noreferrer" className="text-brand hover:underline">React</a></li>
            </ul>
          </section>

        </main>
      </div>
    </div>
  );
}
